import React, { useState, useEffect, useCallback } from 'react';
import apiAnalyticsService from './services/apiAnalyticsService';
import AnalyticsSummaryCards from './components/AnalyticsSummaryCards';
import AnalyticsFilters from './components/AnalyticsFilters';
import APIUsageTrend from './components/APIUsageTrend';
import APIStatusChart from './components/APIStatusChart';
import ResponseTimeChart from './components/ResponseTimeChart';
import MostUsedAPIs from './components/MostUsedAPIs';
import HighestFailureAPIs from './components/HighestFailureAPIs';
import APIAnalyticsTable from './components/APIAnalyticsTable';
import MemoryUsageChart from './components/MemoryUsageChart';
import ServerMonitor from './components/ServerMonitor';
import './css/apiAnalytics.css'; // New custom CSS for the dashboard

const APIAnalytics = () => {
    const [summary, setSummary] = useState(null);
    const [trends, setTrends] = useState([]);
    const [apis, setApis] = useState([]);
    const [latestRequests, setLatestRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('web'); // 'web' or 'mobile'

    const loadData = useCallback(async (filters = {}, isMobile = false) => {
        setLoading(true);
        setError('');
        try {
            const [summaryRes, trendsRes, apisRes, latestRes] = await Promise.all([
                apiAnalyticsService.getSummary(filters, isMobile),
                apiAnalyticsService.getTrends(filters, 'day', isMobile),
                apiAnalyticsService.getApisList(filters, 'calls', 100, isMobile),
                apiAnalyticsService.getLatestRequests(filters, 12, isMobile)
            ]);
            
            setSummary(summaryRes.data);
            setTrends(trendsRes.data);
            setApis(apisRes.data);
            setLatestRequests(latestRes.data);
        } catch (err) {
            console.error('Failed to load analytics:', err);
            setError('Unable to load API analytics.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Initial load and reload on tab change
        loadData({}, activeTab === 'mobile');
    }, [loadData, activeTab]);

    const handleFilterChange = (filters) => {
        loadData(filters, activeTab === 'mobile');
    };

    if (error) {
        return (
            <div className="api-analytics-container">
                <div className="analytics-loading" style={{ color: '#e74c3c' }}>{error}</div>
            </div>
        );
    }

    return (
        <div className="api-analytics-container">
            <div className="api-analytics-header">
                <h2>API Analytics & Monitoring Dashboard</h2>
            </div>
            
            <div style={{ marginBottom: '20px', borderBottom: '1px solid #ecf0f1' }}>
                <button 
                    onClick={() => setActiveTab('web')}
                    style={{
                        padding: '10px 20px', backgroundColor: 'transparent', border: 'none', 
                        borderBottom: activeTab === 'web' ? '3px solid #3498db' : '3px solid transparent',
                        color: activeTab === 'web' ? '#3498db' : '#7f8c8d', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer'
                    }}
                >
                    Web Admin API
                </button>
                <button 
                    onClick={() => setActiveTab('mobile')}
                    style={{
                        padding: '10px 20px', backgroundColor: 'transparent', border: 'none', 
                        borderBottom: activeTab === 'mobile' ? '3px solid #3498db' : '3px solid transparent',
                        color: activeTab === 'mobile' ? '#3498db' : '#7f8c8d', fontWeight: 'bold', fontSize: '16px', cursor: 'pointer',
                        marginLeft: '10px'
                    }}
                >
                    Mobile API
                </button>
            </div>

            {/* <AnalyticsFilters onFilterChange={handleFilterChange} /> */}
            
            {loading ? (
                <div className="analytics-loading">Loading API analytics...</div>
            ) : (
                <>
                    {/* Server Monitoring & Control */}
                    <ServerMonitor isMobile={activeTab === 'mobile'} />

                    {/* Summary Cards */}
                    <AnalyticsSummaryCards summary={summary} />

                    {/* Main Charts Section */}
                    <div className="charts-grid">
                        <APIUsageTrend data={trends} />
                        <APIStatusChart summary={summary} />
                    </div>

                    {/* Response Time Trend */}
                    <div className="charts-grid full-width">
                        <ResponseTimeChart data={latestRequests} />
                    </div>
                    
                    {/* Memory Usage Trend */}
                    <div className="charts-grid full-width">
                        <MemoryUsageChart data={latestRequests} />
                    </div>

                    {/* API Specific Analysis */}
                    <div className="charts-grid half">
                        <MostUsedAPIs apis={apis} activeTab={activeTab} />
                        <HighestFailureAPIs apis={apis} />
                    </div>

                    {/* Details Table */}
                    <APIAnalyticsTable apis={apis} />
                </>
            )}
        </div>
    );
};

export default APIAnalytics;
